import { lazy, Suspense, useEffect, useState } from "react";
import { App as AntApp, Avatar, Button, Layout, Menu, Result, Skeleton, Typography } from "antd";
import {
  AppstoreOutlined,
  ApiOutlined,
  BookOutlined,
  HistoryOutlined,
  BulbOutlined,
  PlusOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  TrophyOutlined
} from "@ant-design/icons";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { consumePostAuthRedirect, ensureAuth } from "./api";
import { PRODUCT_NAME } from "./branding";
import { AgentCenter } from "./pages/AgentCenter";

const CreateAgent = lazy(() => import("./pages/CreateAgent").then((module) => ({ default: module.CreateAgent })));
const AgentEditor = lazy(() => import("./pages/AgentEditor").then((module) => ({ default: module.AgentEditor })));
const AgentDetail = lazy(() => import("./pages/AgentDetail").then((module) => ({ default: module.AgentDetail })));
const Versions = lazy(() => import("./pages/Versions").then((module) => ({ default: module.Versions })));
const Knowledge = lazy(() => import("./pages/Knowledge").then((module) => ({ default: module.Knowledge })));
const CapabilityCenter = lazy(() => import("./pages/CapabilityCenter").then((module) => ({ default: module.CapabilityCenter })));
const Memories = lazy(() => import("./pages/Memories").then((module) => ({ default: module.Memories })));
const ExternalAccess = lazy(() => import("./pages/ExternalAccess").then((module) => ({ default: module.ExternalAccess })));
const AgentEvolution = lazy(() => import("./pages/AgentOptimization").then((module) => ({ default: module.AgentOptimization })));
const ToolApprovals = lazy(() => import("./pages/ToolApprovals").then((module) => ({ default: module.ToolApprovals })));
const ProductOptimization = lazy(() => import("./pages/ProductOptimization").then((module) => ({ default: module.ProductOptimization })));
const CodexUsageLeaderboard = lazy(() => import("./pages/CodexUsageLeaderboard").then((module) => ({ default: module.CodexUsageLeaderboard })));

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<{ name: string; department: string; isAdmin?: boolean }>();
  const [authError, setAuthError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const { message } = AntApp.useApp();

  useEffect(() => {
    ensureAuth()
      .then(({ user }) => {
        setUser(user);
        const resumePath = consumePostAuthRedirect();
        if (resumePath && resumePath !== `${location.pathname}${location.search}`) {
          navigate(resumePath, { replace: true });
        }
      })
      .catch((error) => {
        setAuthError(error.message);
        message.error(error.message);
      })
      .finally(() => setLoading(false));
  }, [location.pathname, location.search, message, navigate]);

  if (loading) return <div className="boot-screen"><Skeleton active /></div>;
  if (authError || !user) {
    return (
      <Result
        status="error"
        title={`无法登录${PRODUCT_NAME}`}
        subTitle={authError || "没有获取到飞书员工身份"}
        extra={<Button type="primary" onClick={() => window.location.reload()}>重新尝试</Button>}
      />
    );
  }

  const selected = location.pathname.startsWith("/knowledge")
    ? "/knowledge"
    : location.pathname.startsWith("/memories")
      ? "/memories"
    : location.pathname.startsWith("/capabilities") || location.pathname.startsWith("/skills")
      ? "/capabilities"
    : location.pathname.startsWith("/tool-approvals")
      ? "/tool-approvals"
    : location.pathname.startsWith("/external-access")
        ? "/external-access"
    : location.pathname.startsWith("/codex-usage")
      ? "/codex-usage"
    : location.pathname.startsWith("/product-optimization")
      ? "/product-optimization"
      : "/agents";
  return (
    <Layout className="app-shell">
      <Layout.Header className="topbar">
        <Link to="/agents" className="brand">
          <span className="brand-mark"><RobotOutlined /></span>
          <Typography.Text strong>{PRODUCT_NAME}</Typography.Text>
        </Link>
        <div className="top-actions">
          {user.isAdmin && (
            <Button icon={<BulbOutlined />} onClick={() => navigate("/product-optimization")}>每日优化</Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/agents/create")}>
            创建 Agent
          </Button>
          <Avatar style={{ background: "#7b61ff" }}>{user?.name?.slice(0, 1)}</Avatar>
          <div className="user-meta">
            <strong>{user?.name}</strong>
            <span>{user?.department || "企业员工"}</span>
          </div>
        </div>
      </Layout.Header>
      <Layout>
        <Layout.Sider width={208} className="sidebar" breakpoint="lg" collapsedWidth="0">
          <Menu
            mode="inline"
            selectedKeys={[selected]}
            items={[
              {
                key: "/agents",
                icon: <AppstoreOutlined />,
                label: <Link to="/agents">Agent 中心</Link>
              },
              {
                key: "/knowledge",
                icon: <BookOutlined />,
                label: <Link to="/knowledge">我的知识库</Link>
              },
              {
                key: "/capabilities",
                icon: <ThunderboltOutlined />,
                label: <Link to="/capabilities">能力市场</Link>
              },
              {
                key: "/memories",
                icon: <HistoryOutlined />,
                label: <Link to="/memories">我的记忆</Link>
              },
              {
                key: "/tool-approvals",
                icon: <SafetyCertificateOutlined />,
                label: <Link to="/tool-approvals">操作确认</Link>
              },
              {
                key: "/codex-usage",
                icon: <TrophyOutlined />,
                label: <Link to="/codex-usage">Token 排行榜</Link>
              },
              {
                key: "/external-access",
                icon: <ApiOutlined />,
                label: <Link to="/external-access">外部接入</Link>
              },
              ...(user.isAdmin ? [{
                key: "/product-optimization",
                icon: <BulbOutlined />,
                label: <Link to="/product-optimization">每日产品优化</Link>
              }] : [])
            ]}
          />
        </Layout.Sider>
        <Layout.Content className="content">
          <Suspense fallback={<div className="boot-screen"><Skeleton active /></div>}>
          <Routes>
            <Route path="/agents" element={<AgentCenter />} />
            <Route path="/agents/create" element={<CreateAgent />} />
            <Route path="/agents/:id/edit" element={<AgentEditor />} />
            <Route path="/agents/:id/detail" element={<AgentDetail />} />
            <Route path="/agents/:id/versions" element={<Versions />} />
            <Route path="/agents/:id/evolution" element={<AgentEvolution />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/capabilities" element={<CapabilityCenter />} />
            <Route path="/skills" element={<Navigate to="/capabilities" replace />} />
            <Route path="/memories" element={<Memories />} />
            <Route path="/external-access" element={<ExternalAccess />} />
            <Route path="/tool-approvals" element={<ToolApprovals />} />
            <Route path="/codex-usage" element={<CodexUsageLeaderboard />} />
            <Route
              path="/product-optimization"
              element={user.isAdmin ? <ProductOptimization /> : <Navigate to="/agents" replace />}
            />
            <Route path="*" element={<Navigate to="/agents" replace />} />
          </Routes>
          </Suspense>
        </Layout.Content>
      </Layout>
      <nav className="mobile-bottom-nav" aria-label="手机端主导航">
        <Link
          to="/agents"
          className={location.pathname.startsWith("/agents") && !location.pathname.startsWith("/agents/create") ? "active" : ""}
        >
          <AppstoreOutlined />
          <span>Agent</span>
        </Link>
        <Link
          to="/knowledge"
          className={location.pathname.startsWith("/knowledge") ? "active" : ""}
        >
          <BookOutlined />
          <span>知识库</span>
        </Link>
        <Link
          to="/capabilities"
          className={location.pathname.startsWith("/capabilities") ? "active" : ""}
        >
          <ThunderboltOutlined />
          <span>能力</span>
        </Link>
        <Link
          to="/memories"
          className={location.pathname.startsWith("/memories") ? "active" : ""}
        >
          <HistoryOutlined />
          <span>记忆</span>
        </Link>
        <Link
          to="/external-access"
          className={location.pathname.startsWith("/external-access") ? "active" : ""}
        >
          <ApiOutlined />
          <span>接入</span>
        </Link>
        <Link
          to="/codex-usage"
          className={location.pathname.startsWith("/codex-usage") ? "active" : ""}
        >
          <TrophyOutlined />
          <span>排行</span>
        </Link>
      </nav>
    </Layout>
  );
}

export function App() {
  return (
    <AntApp>
      <Shell />
    </AntApp>
  );
}
